import { useContext } from "react";
import { LayoutContext } from "../context/LayoutContext";
import { ChevronRight, Infinity, Layers, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import PurchaserClient from "../clients/PurchaserClient";
import VendorDiscoveryIcon from "./Common/CustomIcons/VendorDiscoveryIcon";
import { Modal } from "./DesignSystem";

const BlueGradientText = ({ children, className }: { children: React.ReactNode; className?: string }) => (
    <span className={`bg-linear-to-l from-stone-950 to-blue-600 bg-clip-text text-transparent ${className ?? ""}`}>{children}</span>
);

const FreeTrialWelcomeDialog = () => {
    const { isFreeTrialWelcomeDialogOpen, closeFreeTrialWelcomeDialog } = useContext(LayoutContext);

    const { data: subscriptionData, isLoading } = useQuery({
        queryKey: ["subscriptionData"],
        queryFn: async () => PurchaserClient.GetPlanDetails(),
    });

    if (isLoading) {
        return <div></div>;
    }

    if (!subscriptionData) {
        return null; // No subscription data available
    }

    const { nextInvoiceDate } = subscriptionData;

    const trialEndingDate = new Date(nextInvoiceDate);
    const formattedTrialEndingDate = trialEndingDate.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });

    return (
        <div>
            <Modal
                className="relative px-0 md:w-fit md:max-w-5xl lg:max-w-3/4"
                open={isFreeTrialWelcomeDialogOpen}
                onClose={() => closeFreeTrialWelcomeDialog()}
                showCloseButton={false}
            >
                {/* X button to close */}
                <div className="absolute top-5 right-5 z-50">
                    <X className="h-4 w-4 cursor-pointer stroke-stone-400" onClick={() => closeFreeTrialWelcomeDialog()} />
                </div>
                <div className="relative -mt-5 mb-4 h-[283px] overflow-hidden">
                    <img
                        src="https://app.purchaser.ai/images/shapes/radial-blue-lines.png"
                        alt="Radial Blue Lines"
                        className="absolute top-0 left-0 z-10 w-full rounded-t-2xl opacity-70"
                    />
                    <div className="relative z-20 mt-16 mb-2 flex items-center justify-center gap-2">
                        <BlueGradientText className="text-2xl font-medium">Professional Tier</BlueGradientText>
                        <span className="rounded-xl bg-linear-to-l from-stone-950 to-blue-600 px-1.5 text-white">Trial</span>
                    </div>
                    <div className="relative z-20 mx-auto mb-8 max-w-2xl bg-linear-to-l from-stone-950 to-blue-600 bg-clip-text text-center text-transparent">
                        <span className="text-[48px] leading-[125%] font-medium">Modern sourcing tools for purchasing agents</span>
                    </div>
                </div>
                <div className="mb-6">
                    <div className="mb-1 text-center text-2xl font-medium">Welcome to Your Free Trial</div>
                    <div className="text-center text-xl">You're now on a free trial of the Professional plan.</div>
                    <div className="text-center text-xl">Enjoy full access to all features. No limitations.</div>
                </div>
                <div className="mb-6 items-center px-8">
                    <div className="grid grid-cols-1 gap-6 rounded-2xl border border-blue-200 p-3 md:grid-cols-[1fr_1fr_1fr]">
                        <div>
                            <div className="mb-2 flex h-fit w-fit justify-center rounded-tr-2xl rounded-br-2xl bg-linear-to-r from-white to-blue-50 p-3">
                                <Infinity className="h-10 w-10 stroke-blue-700" />
                            </div>
                            <BlueGradientText className="font-medium">Unlimited Usage</BlueGradientText>
                            <div>No limitations on how many RFQs you can send, documents you can process, or vendors you can manage.</div>
                        </div>
                        <div>
                            <div className="mb-2 flex h-fit w-fit justify-center rounded-tr-2xl rounded-br-2xl bg-linear-to-r from-white to-blue-50 p-3">
                                <Layers className="h-10 w-10 stroke-blue-700" />
                            </div>
                            <BlueGradientText className="font-medium">Advanced Features</BlueGradientText>
                            <div>Create RFQs based on templates or prior emails, auto-detect bids, and get smart vendor matches for RFQs.</div>
                        </div>
                        <div>
                            <div className="mb-2 flex h-fit w-fit justify-center rounded-tr-2xl rounded-br-2xl bg-linear-to-r from-white to-blue-50 p-3">
                                <VendorDiscoveryIcon.Blue className="h-10 w-10" />
                            </div>
                            <BlueGradientText className="font-medium">Easy Integration</BlueGradientText>
                            <div>Connect your email inbox and manage your complex procurement network. Built to fit your existing workflows.</div>
                        </div>
                    </div>
                </div>
                <button
                    onClick={() => closeFreeTrialWelcomeDialog()}
                    className="mx-auto mb-6 flex items-center rounded px-2 py-1 font-medium hover:bg-stone-50 active:bg-stone-100"
                >
                    Get Started
                    <ChevronRight className="h-4 w-4 stroke-stone-500" color="clear" />
                </button>
                <div className="text-center text-stone-500">
                    Your trial will end on {formattedTrialEndingDate}. We'll send a reminder before that happens.
                </div>
            </Modal>
        </div>
    );
};

export default FreeTrialWelcomeDialog;
